/**
 * Regras puras de filtragem dos relatórios (aba por aba). Sem React, sem
 * API: recebem as listas já carregadas e devolvem o recorte. A tela e o
 * export (CSV/PDF) usam exatamente estas funções — o que aparece na tela é
 * o que sai no arquivo.
 */
import type { OrcamentoResumo } from '@/features/orcamentos/api/orcamentosApi';
import {
  ETAPA_CANCELADO,
  ETAPA_FECHADO,
  ETAPAS_TERMINAIS,
} from '@/features/orcamentos/api/etapasKanban';
import type { UsoDetalhado } from '@/features/relatorios/api/relatoriosApi';
import type { MovimentacaoEstoqueResumo } from '@/features/estoque/api/estoqueApi';
import type { ProdutoResumo } from '@/features/produtos/api/produtosApi';
import { normalizarBusca } from '@/shared/ui/SearchInput';

// ---------------------------------------------------------------------------
// Status da O.S.
// ---------------------------------------------------------------------------

/** Valor do select de status que significa "todas as etapas". */
export const STATUS_TODOS = '*';
/** Valor do select de status que significa "todas, menos Cancelado". */
export const STATUS_EXCETO_CANCELADAS = '!cancelado';

/**
 * Filtro de status da O.S.: `STATUS_TODOS`, `STATUS_EXCETO_CANCELADAS` ou o
 * id de uma etapa do kanban (ex.: `FechadoPago` = "somente fechadas/pagas").
 */
export type FiltroStatusOS = string;

export function statusPassa(filtro: FiltroStatusOS, status: string): boolean {
  if (filtro === STATUS_TODOS || filtro === '') return true;
  if (filtro === STATUS_EXCETO_CANCELADAS) return status !== ETAPA_CANCELADO;
  return status === filtro;
}

/** Texto do filtro de status para chips e rótulo do relatório. */
export function rotuloStatusOS(filtro: FiltroStatusOS, rotuloDe: (id: string) => string): string {
  if (filtro === STATUS_TODOS || filtro === '') return 'Todos (inclui canceladas)';
  if (filtro === STATUS_EXCETO_CANCELADAS) return 'Todas exceto canceladas';
  if (filtro === ETAPA_FECHADO) return 'Somente fechadas/pagas';
  return rotuloDe(filtro);
}

// ---------------------------------------------------------------------------
// Período e data base
// ---------------------------------------------------------------------------

export type BaseData = 'abertura' | 'fechamento';

export const ROTULO_BASE_DATA: Record<BaseData, string> = {
  abertura: 'Data de abertura',
  fechamento: 'Data de fechamento',
};

/** Limites do período `YYYY-MM-DD` no fuso local (dia inteiro). */
export function limitesPeriodo(inicio: string, fim: string): { inicio: Date; fim: Date } {
  return {
    inicio: new Date(`${inicio}T00:00:00`),
    fim: new Date(`${fim}T23:59:59.999`),
  };
}

/**
 * Data que posiciona a O.S. no período.
 *
 * - `abertura`: a data da O.S.
 * - `fechamento`: o momento em que ela foi fechada/paga (`finalizadoEm`).
 *   O.S. que nunca fechou não tem data de fechamento → `null` (fica de fora).
 *   O.S. fechada sem `finalizadoEm` (dado antigo, ou backend sem o campo)
 *   cai na data de abertura.
 */
export function dataBaseOrcamento(
  o: Pick<OrcamentoResumo, 'data' | 'status'> & { finalizadoEm?: string | null },
  base: BaseData,
): string | null {
  if (base === 'abertura') return o.data;
  if (o.finalizadoEm) return o.finalizadoEm;
  return o.status === ETAPA_FECHADO ? o.data : null;
}

export function noPeriodo(iso: string | null, inicio: string, fim: string): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const lim = limitesPeriodo(inicio, fim);
  return d >= lim.inicio && d <= lim.fim;
}

// ---------------------------------------------------------------------------
// Ordens de serviço
// ---------------------------------------------------------------------------

/** Valor do filtro de responsável que significa "O.S. sem responsável". */
export const SEM_RESPONSAVEL = '!sem';

export type FiltroPrazo = '' | 'atrasadas' | 'no-prazo';

export const ROTULO_PRAZO: Record<Exclude<FiltroPrazo, ''>, string> = {
  atrasadas: 'Atrasadas',
  'no-prazo': 'No prazo',
};

/**
 * Atrasada = prazo de entrega num dia anterior a hoje (fuso local, como o
 * destaque do kanban) e O.S. ainda numa etapa não terminal.
 */
export function estaAtrasada(
  o: Pick<OrcamentoResumo, 'prazoEntrega' | 'status'>,
  agora: Date = new Date(),
): boolean {
  if (ETAPAS_TERMINAIS.includes(o.status)) return false;
  if (!o.prazoEntrega) return false;
  const prazo = new Date(o.prazoEntrega);
  if (Number.isNaN(prazo.getTime())) return false;
  const inicioHoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const diaPrazo = new Date(prazo.getFullYear(), prazo.getMonth(), prazo.getDate());
  return diaPrazo.getTime() < inicioHoje.getTime();
}

export type FiltrosOrcamentos = {
  status: FiltroStatusOS;
  /** '' = todos; `SEM_RESPONSAVEL`; ou id do colaborador. */
  responsavel: string;
  prazo: FiltroPrazo;
  busca: string;
};

export function filtrarOrcamentos(
  orcamentos: OrcamentoResumo[],
  f: FiltrosOrcamentos,
  nomeCliente: (id: string) => string,
  agora: Date = new Date(),
): OrcamentoResumo[] {
  const q = normalizarBusca(f.busca.trim());
  return orcamentos.filter((o) => {
    if (!statusPassa(f.status, o.status)) return false;
    if (f.responsavel === SEM_RESPONSAVEL) {
      if (o.colaboradorResponsavelId) return false;
    } else if (f.responsavel && o.colaboradorResponsavelId !== f.responsavel) {
      return false;
    }
    if (f.prazo === 'atrasadas' && !estaAtrasada(o, agora)) return false;
    if (f.prazo === 'no-prazo' && estaAtrasada(o, agora)) return false;
    if (q === '') return true;
    return normalizarBusca(
      `${o.numero} ${o.veiculoDescricao} ${o.status} ${nomeCliente(o.clienteId)}`,
    ).includes(q);
  });
}

/** Responsáveis que aparecem nas O.S. (para o select), em ordem alfabética. */
export function responsaveisDisponiveis(
  orcamentos: OrcamentoResumo[],
): Array<{ id: string; nome: string }> {
  const mapa = new Map<string, string>();
  for (const o of orcamentos) {
    if (o.colaboradorResponsavelId) {
      mapa.set(o.colaboradorResponsavelId, o.colaboradorResponsavelNome ?? '(sem nome)');
    }
  }
  return ordenarPorNome(Array.from(mapa, ([id, nome]) => ({ id, nome })));
}

/** Números da visão geral, com Canceladas separadas de "Em aberto". */
export function resumoVisaoGeral(orcamentos: OrcamentoResumo[]) {
  let fechadas = 0;
  let faturado = 0;
  let canceladas = 0;
  let valorCanceladas = 0;
  let emAberto = 0;
  let valorEmAberto = 0;
  for (const o of orcamentos) {
    if (o.status === ETAPA_FECHADO) {
      fechadas += 1;
      faturado += o.total;
    } else if (o.status === ETAPA_CANCELADO) {
      canceladas += 1;
      valorCanceladas += o.total;
    } else {
      emAberto += 1;
      valorEmAberto += o.total;
    }
  }
  return {
    total: orcamentos.length,
    fechadas,
    faturado,
    emAberto,
    valorEmAberto,
    canceladas,
    valorCanceladas,
    ticketMedio: fechadas === 0 ? 0 : faturado / fechadas,
  };
}

// ---------------------------------------------------------------------------
// Peças / Serviços / Colaboradores (itens das O.S.)
// ---------------------------------------------------------------------------

/** Valor do filtro de colaborador que significa "item sem colaborador". */
export const SEM_COLABORADOR = '!sem';

export type FiltroTipoItem = '' | 'Produto' | 'Servico';

export type FiltrosUsos = {
  status: FiltroStatusOS;
  /** '' = todos; `SEM_COLABORADOR`; ou id do colaborador. */
  colaborador: string;
  somenteComDesconto: boolean;
  /** Peça/serviço específico ('' = todos). */
  referenciaId: string;
  tipo: FiltroTipoItem;
  busca: string;
};

export function filtrarUsos(usos: UsoDetalhado[], f: FiltrosUsos): UsoDetalhado[] {
  const q = normalizarBusca(f.busca.trim());
  return usos.filter((u) => {
    if (!statusPassa(f.status, u.orcamentoStatus)) return false;
    if (f.tipo && u.tipo !== f.tipo) return false;
    if (f.referenciaId && u.referenciaId !== f.referenciaId) return false;
    if (f.colaborador === SEM_COLABORADOR) {
      if (u.colaboradorId) return false;
    } else if (f.colaborador && u.colaboradorId !== f.colaborador) {
      return false;
    }
    if (f.somenteComDesconto && !(u.valorDesconto > 0)) return false;
    if (q === '') return true;
    return normalizarBusca(
      `${u.colaboradorNome ?? ''} ${u.descricao} ${u.orcamentoNumero} ${u.clienteNome}`,
    ).includes(q);
  });
}

/** Itens distintos (peças ou serviços) das linhas — para o select. */
export function itensDisponiveis(usos: UsoDetalhado[]): Array<{ id: string; descricao: string }> {
  const mapa = new Map<string, string>();
  for (const u of usos) mapa.set(u.referenciaId, u.descricao);
  return Array.from(mapa, ([id, descricao]) => ({ id, descricao })).sort((a, b) =>
    a.descricao.localeCompare(b.descricao, 'pt-BR'),
  );
}

/** Colaboradores que aparecem nas linhas — para o select. */
export function colaboradoresDosUsos(usos: UsoDetalhado[]): Array<{ id: string; nome: string }> {
  const mapa = new Map<string, string>();
  for (const u of usos) {
    if (u.colaboradorId && u.colaboradorNome) mapa.set(u.colaboradorId, u.colaboradorNome);
  }
  return ordenarPorNome(Array.from(mapa, ([id, nome]) => ({ id, nome })));
}

export type LinhaRanking = {
  referenciaId: string;
  descricao: string;
  quantidade: number;
  /** Nº de O.S. distintas em que o item aparece. */
  ordens: number;
  desconto: number;
  valor: number;
};

/** Agrupa os itens por peça/serviço; maior valor primeiro. */
export function rankingUsos(usos: UsoDetalhado[]): LinhaRanking[] {
  const mapa = new Map<string, LinhaRanking & { ids: Set<string> }>();
  for (const u of usos) {
    const chave = `${u.tipo}:${u.referenciaId}`;
    const atual =
      mapa.get(chave) ??
      {
        referenciaId: u.referenciaId,
        descricao: u.descricao,
        quantidade: 0,
        ordens: 0,
        desconto: 0,
        valor: 0,
        ids: new Set<string>(),
      };
    atual.quantidade += u.quantidade;
    atual.desconto += u.valorDesconto;
    atual.valor += u.subtotal;
    atual.ids.add(u.orcamentoId);
    mapa.set(chave, atual);
  }
  return Array.from(mapa.values())
    .map(({ ids, ...linha }) => ({ ...linha, ordens: ids.size }))
    .sort(
      (a, b) =>
        b.valor - a.valor ||
        b.quantidade - a.quantidade ||
        a.descricao.localeCompare(b.descricao, 'pt-BR'),
    );
}

export type ResumoColaborador = {
  colaboradorId: string;
  colaboradorNome: string;
  pecas: number;
  servicos: number;
  quantidade: number;
  total: number;
};

export function resumirColaboradores(usos: UsoDetalhado[]): ResumoColaborador[] {
  const mapa = new Map<string, ResumoColaborador>();
  for (const u of usos) {
    if (!u.colaboradorId || !u.colaboradorNome) continue;
    const atual = mapa.get(u.colaboradorId) ?? {
      colaboradorId: u.colaboradorId,
      colaboradorNome: u.colaboradorNome,
      pecas: 0,
      servicos: 0,
      quantidade: 0,
      total: 0,
    };
    if (u.tipo === 'Produto') atual.pecas += 1;
    else atual.servicos += 1;
    atual.quantidade += u.quantidade;
    atual.total += u.subtotal;
    mapa.set(u.colaboradorId, atual);
  }
  return Array.from(mapa.values()).sort((a, b) =>
    a.colaboradorNome.localeCompare(b.colaboradorNome, 'pt-BR'),
  );
}

// ---------------------------------------------------------------------------
// Estoque
// ---------------------------------------------------------------------------

export type SituacaoEstoqueFiltro = '' | 'com' | 'sem' | 'negativo';
export type CadastroProdutoFiltro = 'ativos' | 'inativos' | 'todos';
export type TipoMovimentoEstoqueFiltro = '' | 'Entrada' | 'Saida';
export type OrigemMovimentoFiltro = '' | MovimentacaoEstoqueResumo['origemTipo'];

export const ROTULO_SITUACAO_ESTOQUE: Record<Exclude<SituacaoEstoqueFiltro, ''>, string> = {
  com: 'Com estoque',
  sem: 'Sem estoque',
  negativo: 'Saldo negativo',
};

export const ROTULO_CADASTRO: Record<CadastroProdutoFiltro, string> = {
  ativos: 'Ativos',
  inativos: 'Inativos',
  todos: 'Ativos e inativos',
};

export const ROTULO_ORIGEM: Record<MovimentacaoEstoqueResumo['origemTipo'], string> = {
  Orcamento: 'O.S.',
  Estoque: 'Manual',
  EstornoOrcamento: 'Estorno de O.S.',
  Produto: 'Cadastro',
};

/** Situação de um saldo (real — negativo aparece como negativo). */
export function situacaoSaldo(saldo: number): Exclude<SituacaoEstoqueFiltro, ''> {
  if (saldo < 0) return 'negativo';
  return saldo > 0 ? 'com' : 'sem';
}

export function cadastroPassa(filtro: CadastroProdutoFiltro, ativo: boolean): boolean {
  if (filtro === 'todos') return true;
  return filtro === 'ativos' ? ativo : !ativo;
}

export function filtrarProdutosEstoque(
  produtos: ProdutoResumo[],
  f: { situacao: SituacaoEstoqueFiltro; cadastro: CadastroProdutoFiltro; busca: string },
): ProdutoResumo[] {
  const q = normalizarBusca(f.busca.trim());
  return produtos.filter((p) => {
    if (!cadastroPassa(f.cadastro, p.ativo)) return false;
    const saldo = p.estoqueQuantidade ?? 0;
    // "Sem estoque" inclui os negativos (não há peça na prateleira);
    // "Saldo negativo" isola só os que ficaram abaixo de zero.
    if (f.situacao === 'com' && !(saldo > 0)) return false;
    if (f.situacao === 'sem' && saldo > 0) return false;
    if (f.situacao === 'negativo' && !(saldo < 0)) return false;
    if (!q) return true;
    return normalizarBusca(`${p.codigo} ${p.nome}`).includes(q);
  });
}

export function filtrarMovimentacoes(
  movimentacoes: MovimentacaoEstoqueResumo[],
  f: {
    inicio: string;
    fim: string;
    tipo: TipoMovimentoEstoqueFiltro;
    origem: OrigemMovimentoFiltro;
    ocultarEstornadas: boolean;
    busca: string;
  },
): MovimentacaoEstoqueResumo[] {
  const q = normalizarBusca(f.busca.trim());
  return movimentacoes.filter((m) => {
    if (!noPeriodo(m.criadoEm, f.inicio, f.fim)) return false;
    if (f.tipo && m.tipo !== f.tipo) return false;
    if (f.origem && m.origemTipo !== f.origem) return false;
    if (f.ocultarEstornadas && m.estornada) return false;
    if (!q) return true;
    return normalizarBusca(
      `${m.produtoCodigo} ${m.produtoNome} ${m.tipo} ${m.motivo} ${ROTULO_ORIGEM[m.origemTipo] ?? m.origemTipo}`,
    ).includes(q);
  });
}

// ---------------------------------------------------------------------------

function ordenarPorNome<T extends { nome: string }>(lista: T[]): T[] {
  return lista.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}
